/**
 * Support sessions: an Adaptive Catalog person signed in to a customer organization from the partner portal.
 *
 * A support session lives in exactly one tab. Its details are kept in sessionStorage, which is per tab and gone
 * when the tab closes, so a reload keeps the session but nothing leaks into the person's own tabs. The same rule
 * covers the rest of the app's persisted state while the tab is a support tab (see rootReducer): writing the
 * customer's organization or searches into localStorage would carry them into the person's next ordinary session.
 */
export interface ISupportSession {
    sessionKey: string;
    /** The session's public id (not a credential). Absent on sessions started before it was handed out. */
    sessionId?: string;
    /** ISO timestamp. Fixed: support sessions are never refreshed. */
    expiresAt: string;
    organization: string;
    organizationName?: string;
    /** The synthetic username the session acts under (support:<actor>). */
    username: string;
    actor: string;
    actorName?: string;
}

export const SUPPORT_LOGIN_PATH = '/support-login';
/** ?state= values for the support-login page once a session is over. */
export const SUPPORT_SESSION_ENDED = 'ended';
export const SUPPORT_SESSION_REVOKED = 'revoked';

/** The first eight characters, upper-cased: enough to tell sessions apart at a glance and to match the portal's log. */
export const shortSessionId = (sessionId?: string) => sessionId ? sessionId.slice(0, 8).toUpperCase() : undefined;
const STORAGE_KEY = 'ac.supportSession';

const isExpired = (session: ISupportSession) => new Date(session.expiresAt).getTime() <= Date.now();

export const readSupportSession = (): ISupportSession | undefined => {
    try {
        const raw = window.sessionStorage?.getItem(STORAGE_KEY);
        if (!raw) return undefined;
        const session = JSON.parse(raw) as ISupportSession;
        if (!session?.sessionKey || !session.organization || isExpired(session)) {
            window.sessionStorage.removeItem(STORAGE_KEY);
            return undefined;
        }
        return session;
    } catch {
        return undefined;
    }
};

export const writeSupportSession = (session: ISupportSession) => {
    try {
        window.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
        // Without sessionStorage the session still works; it just won't survive a reload.
    }
};

export const clearSupportSession = () => {
    try {
        window.sessionStorage?.removeItem(STORAGE_KEY);
    } catch {
        // Nothing to clear.
    }
};

// The session is over: forget it and leave the tab on the "session over" page, with a full reload so nothing the
// session loaded stays on screen.
const supportSessionLost = () => {
    clearSupportSession();
    window.location.replace(`${SUPPORT_LOGIN_PATH}?state=${SUPPORT_SESSION_REVOKED}`);
};

let verifying: Promise<void> | undefined;

/**
 * Called on any 401 in a support tab. A 401 alone does not mean the session is over: some routes answer 401 for a
 * permission the support role lacks (API keys, for one). So it asks the session-status route, which only the session
 * key itself can satisfy, and ends the tab's session only if that is refused too: ended from the partner portal, or
 * expired. Plain fetch on purpose, so the check can never re-enter the API layer whose 401 triggered it.
 * A no-op outside support tabs, and at most one check in flight.
 */
export const verifySupportSession = () => {
    const session = readSupportSession();
    if (!session || verifying) return;
    const headers: Record<string, string> = {'x-session-key': session.sessionKey};
    if (window.config.apiVersion) headers['x-api-version'] = window.config.apiVersion.toString();
    verifying = fetch(`${window.config.baseUrl}/api/keyAuth/support/session`, {headers})
        .then((response) => { if (response.status === 401) supportSessionLost(); })
        .catch(() => { /* Unreachable API: not evidence the session ended. The next check tries again. */ })
        .finally(() => { verifying = undefined; });
};

/**
 * Whether this tab is, or is about to become, a support tab. Read once at startup to choose where redux-persist
 * writes, so it has to answer before any React code runs.
 */
export const isSupportTab = (): boolean =>
    window.location.pathname === SUPPORT_LOGIN_PATH || readSupportSession() !== undefined;
